import React from 'react';

/**
 * React Error Boundary component.
 *
 * Catches render errors anywhere in the child component tree and
 * displays a user-friendly fallback UI instead of a blank screen.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // Log the error for debugging / monitoring
    console.error('[ErrorBoundary] Uncaught error:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      const isDev =
        process.env.NODE_ENV === 'development' ||
        import.meta.env?.DEV;

      // Branded fallback. Styled entirely via `wareshare-theme.css`
      // (.ws-error-shell + .ws-error-card + .ws-error-icon) so the
      // colors track BrandingContext overrides and live in one place
      // instead of inline JS objects.
      return (
        <div className="ws-error-shell">
          <div className="ws-error-card">
            <div className="ws-error-icon" aria-hidden="true">⚠</div>
            <h1 style={{
              fontSize: 'var(--ws-text-2xl)', fontWeight: 700,
              color: 'var(--ws-text-primary)', marginBottom: '0.5rem',
            }}>
              Something went wrong
            </h1>
            <p style={{
              color: 'var(--ws-text-secondary)',
              lineHeight: 1.6, marginBottom: '1.75rem',
            }}>
              An unexpected error occurred. Please try reloading the page or
              navigating back to the dashboard.
            </p>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={this.handleGoHome}
                className="btn btn-primary btn-gradient"
              >
                Go to Dashboard
              </button>
              <button
                type="button"
                onClick={this.handleReload}
                className="btn btn-outline-primary"
              >
                Reload page
              </button>
            </div>

            {isDev && this.state.error && (
              <details style={{
                marginTop: '2rem', textAlign: 'left',
                background: 'var(--ws-danger-surface)',
                border: '1px solid var(--ws-danger-border)',
                borderRadius: 'var(--ws-radius-sm)',
                padding: '1rem', fontSize: '0.8rem',
                color: 'var(--ws-danger)',
                maxHeight: 300, overflow: 'auto',
              }}>
                <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                  Error details (dev only)
                </summary>
                <pre style={{ whiteSpace: 'pre-wrap', marginTop: '0.5rem' }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
